import { useState, useEffect, useRef } from 'react';
import { Label } from '@radix-ui/react-label';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { CheckIcon, CaretDownIcon } from '@phosphor-icons/react';

type FormComboboxOption = {
  value: string;
  label: string;
};

type FormComboboxProps = {
  id: string;
  name?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  options: FormComboboxOption[];
  helperText?: string;
  errorText?: Array<string | undefined> | string;
  disabled?: boolean;
  className?: string;
  forceShowErrors?: boolean;
  isTouched?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

export function FormCombobox({
  id,
  name,
  label,
  value = '',
  placeholder,
  options,
  helperText,
  errorText,
  disabled,
  className,
  forceShowErrors = false,
  isTouched = false,
  onChange,
  onBlur,
}: FormComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const cleanErrors = Array.isArray(errorText)
    ? (errorText.filter(Boolean) as string[])
    : errorText
      ? [errorText]
      : [];

  const showErrors = forceShowErrors || isTouched;
  const hasErrors = cleanErrors.length > 0;

  // Find matching option for current value to display label
  const selectedOption = options.find(
    (opt) => opt.value.toLowerCase() === value.toLowerCase()
  );
  const displayValue = selectedOption ? selectedOption.label : value;

  // Sync input value when prop value changes
  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  // Filter options based on search query (only when actively searching)
  const filteredOptions = searchQuery
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          option.value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const handleSelect = (selectedValue: string) => {
    const option = options.find((opt) => opt.value === selectedValue);
    if (option) {
      setInputValue(option.label);
      onChange(option.value);
    } else {
      setInputValue(selectedValue);
      onChange(selectedValue);
    }
    setSearchQuery('');
    setOpen(false);
    onBlur?.();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchQuery(newValue);
    onChange(newValue);
    if (!open) {
      setOpen(true);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label ? (
        <Label className="text-large text-slate-50" htmlFor={id}>
          {label}
        </Label>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <div ref={containerRef} className="relative">
          <PopoverPrimitive.Anchor asChild>
            <Input
              id={id}
              name={name}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => {
                setSearchQuery('');
                setOpen(true);
              }}
              onBlur={(e) => {
                if (!containerRef.current?.contains(e.relatedTarget)) {
                  setOpen(false);
                  onBlur?.();
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
              aria-expanded={open}
              aria-invalid={showErrors && hasErrors}
              className={cn(
                'flex w-full rounded-md border border-purple-400 bg-background px-3 py-2 pr-10 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                showErrors && hasErrors && 'border-red-500'
              )}
            />
          </PopoverPrimitive.Anchor>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <CaretDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </div>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Prevent close when clicking on the input/anchor
            if (containerRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <ul className="max-h-[300px] overflow-y-auto p-1 bg-slate-700">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected =
                  value.toLowerCase() === option.value.toLowerCase();
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className="relative h-12 flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-body outline-none bg-inherit text-slate-50"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(option.value)}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {option.label}
                  </li>
                );
              })
            ) : inputValue ? (
              <li
                className="cursor-pointer px-2 py-1.5 text-body hover:bg-accent hover:text-accent-foreground rounded-sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(inputValue)}
              >
                Use &quot;{inputValue}&quot;
              </li>
            ) : (
              <li className="py-6 text-center text-body text-muted-foreground">
                No options found.
              </li>
            )}
          </ul>
        </PopoverContent>
      </Popover>

      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
      {showErrors && hasErrors ? (
        <p className="text-sm text-red-500">{cleanErrors.join(', ')}</p>
      ) : null}
    </div>
  );
}

